/**
 * Account notices that sit above the app: a pending recovery of this
 * account (user-management §2.5: every signed-in device is told at once and
 * can cancel; a device request can be approved here). Shown only from the
 * server's NX_RECOVERY_PENDING (or READY), cleared when the server says so
 * or after our own cancel or approve succeeded.
 */
import { useState } from 'react';
import { useT } from '../i18n';
import { api } from '../lib/api';
import { useData } from '../store/data';
import { useUm } from '../store/um';
import { Btn, Icon } from '../ui/kit';
import { toast } from '../ui/overlay';
import { runAction } from '../mod/actions';

export function RecoveryAlert() {
  const { t, fmt } = useT();
  const rec = useUm((s) => s.recovery);
  const mfa = useData((d) => !!d.me?.mfa_enabled);
  const [busy, setBusy] = useState<'cancel' | 'approve' | null>(null);
  if (!rec) return null;
  const method = t(rec.path === 'code' ? 'um.recovery.method.code' : 'um.recovery.method.device');
  const where = rec.device_name
    ? rec.approx_location
      ? t('um.recovery.where', { device: rec.device_name, city: rec.approx_location })
      : t('um.recovery.whereDevice', { device: rec.device_name })
    : null;
  const cancel = async () => {
    setBusy('cancel');
    const r = await runAction(() => api.um.recoveryCancel({ request_id: rec.id }));
    setBusy(null);
    if (!r.ok) return;
    useUm.setState({ recovery: null });
    toast(t('um.recovery.cancelDone'), { icon: 'shield-check', tone: 'ok' });
  };
  const approve = async () => {
    setBusy('approve');
    const r = await runAction(() => api.um.recoveryApprove({ request_id: rec.id }), {
      challenge: { reason: 'account', level: mfa ? 'second_factor' : 'password' },
    });
    setBusy(null);
    if (!r.ok) return;
    useUm.setState({ recovery: null });
    toast(t('um.recovery.approved'), { icon: 'check', tone: 'ok' });
  };
  return (
    <div className="ns-alertbar nx-glass strong" role="alert">
      <Icon name="shield" />
      <div className="ns-alertbar-t">
        <b>{t('recovery.alert', { method })}</b>
        {where ? <span className="ns-muted">{where}</span> : null}
        {rec.not_before ? (
          <span>
            {t('recovery.wait', { date: fmt.date(rec.not_before), time: fmt.time(rec.not_before) })}
          </span>
        ) : null}
      </div>
      <div className="ns-alertbar-acts">
        {rec.path === 'device' && rec.state === 'pending' ? (
          <Btn kind="ghost" size="sm" busy={busy === 'approve'} onClick={() => void approve()}>
            {t('um.recovery.approve')}
          </Btn>
        ) : null}
        <Btn kind="danger" size="sm" busy={busy === 'cancel'} onClick={() => void cancel()}>
          {t('recovery.cancel')}
        </Btn>
      </div>
    </div>
  );
}
